/**
 * Greeting Card Toggle & Pricing
 * Handles the collapsible greeting card service.
 *
 * Features:
 * - Toggle textarea visibility on checkbox change
 * - Enable/disable textarea when checked/unchecked
 * - Smooth slide animation
 * - Pricing will be handled via Shopify cart (requires greeting card product)
 */

console.log('🎨 greeting-card.js file loaded');

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    GREETING_CARD_VARIANT_ID: Number(window.florafyGreetingCard?.variantId),
    GREETING_CARD_PRICE: Number(window.florafyGreetingCard?.price),
    ANIMATION_DURATION: 300 // ms
  };

  /**
   * Initialize greeting card toggle functionality
   */
  function initGreetingCardToggle() {
    // Find ALL greeting card toggles (there may be multiple on the page)
    const toggles = document.querySelectorAll('.js-greeting-card-toggle');

    if (toggles.length === 0) return;

    toggles.forEach(toggle => {
      const section = toggle.closest('.greeting-card-section');
      if (!section) return;

      const content = section.querySelector('.js-greeting-card-content');
      const textarea = section.querySelector('.js-greeting-card-message');

      if (!content || !textarea) {
        console.warn('Greeting card elements not found for toggle');
        return;
      }

      // Handle checkbox change
      toggle.addEventListener('change', function() {
        handleToggleChange(this.checked, content, textarea, section);
      });

      // Clear error when user starts typing
      textarea.addEventListener('input', function() {
        if (this.value.trim()) {
          clearGreetingCardError(this);
        }
      });

      // Prevent label click from closing when clicking inside textarea
      content.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    });
  }

  /**
   * Check if a greeting card already exists in the cart
   * Returns the existing card's message or null if none found
   */
  async function checkExistingGreetingCard() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const existingCard = cart.items.find(item =>
        item.variant_id === CONFIG.GREETING_CARD_VARIANT_ID
      );

      if (existingCard && existingCard.properties) {
        return existingCard.properties['_kaartje_bericht'] || true;
      }
      return null;
    } catch (error) {
      console.error('Error checking cart for greeting card:', error);
      return null;
    }
  }

  /**
   * Show warning that a greeting card already exists in cart
   */
  function showExistingCardWarning(section, existingMessage) {
    let warningEl = section.querySelector('.js-greeting-card-existing-warning');

    if (!warningEl) {
      // Create warning element if it doesn't exist
      warningEl = document.createElement('div');
      warningEl.className = 'js-greeting-card-existing-warning';
      warningEl.style.cssText = 'background-color: #FEF3CD; border: 1px solid #C29763; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; font-size: 0.9rem; color: var(--color-primary-dark, #463B33);';

      const content = section.querySelector('.js-greeting-card-content');
      if (content) {
        content.insertBefore(warningEl, content.firstChild);
      }
    }

    const messagePreview = typeof existingMessage === 'string' && existingMessage.length > 0
      ? `"${existingMessage.substring(0, 30)}${existingMessage.length > 30 ? '...' : ''}"`
      : '';

    const t = window.translations?.greetingCard || {};
    warningEl.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 8px;">
        <i class="fa-solid fa-circle-info" style="color: #C29763; margin-top: 2px;"></i>
        <div>
          <strong>${t.existingWarning || 'Er zit al een kaartje in je winkelmand'}</strong>
          ${messagePreview ? `<br><span style="opacity: 0.8; font-size: 0.85rem;">${(t.currentMessage || 'Huidig bericht: {{ message }}').replace('{{ message }}', messagePreview)}</span>` : ''}
          <br><span style="opacity: 0.8; font-size: 0.85rem;">${t.willReplace || 'Een nieuw bericht zal het bestaande vervangen.'}</span>
        </div>
      </div>
    `;
    warningEl.style.display = 'block';
  }

  /**
   * Hide the existing card warning
   */
  function hideExistingCardWarning(section) {
    const warningEl = section.querySelector('.js-greeting-card-existing-warning');
    if (warningEl) {
      warningEl.style.display = 'none';
    }
  }

  /**
   * Handle toggle state change
   */
  async function handleToggleChange(isChecked, content, textarea, section) {
    if (isChecked) {
      // Expand and enable
      slideDown(content);
      textarea.removeAttribute('disabled');
      textarea.focus(); // Auto-focus for better UX
      // Clear any previous error state
      clearGreetingCardError(textarea);

      // Check if greeting card already exists in cart
      const existingMessage = await checkExistingGreetingCard();
      if (existingMessage) {
        showExistingCardWarning(section, existingMessage);
      } else {
        hideExistingCardWarning(section);
      }
    } else {
      // Collapse and disable
      slideUp(content);
      textarea.setAttribute('disabled', 'disabled');
      textarea.value = ''; // Clear message when unchecked
      // Clear any error state
      clearGreetingCardError(textarea);
      // Hide warning
      hideExistingCardWarning(section);
    }
  }

  /**
   * Show greeting card validation error
   */
  function showGreetingCardError(textarea) {
    if (!textarea) return;
    const container = textarea.closest('.greeting-card-content');
    if (!container) return;

    const errorEl = container.querySelector('.js-greeting-card-error');
    const hintEl = container.querySelector('.greeting-card-required-hint');

    // Show error, hide hint
    if (errorEl) errorEl.style.display = 'block';
    if (hintEl) hintEl.style.display = 'none';

    // Add error styling to textarea
    textarea.style.borderColor = '#dc3545';
    textarea.style.boxShadow = '0 0 0 0.2rem rgba(220, 53, 69, 0.15)';

    // Focus textarea
    textarea.focus();
  }

  /**
   * Clear greeting card validation error
   */
  function clearGreetingCardError(textarea) {
    if (!textarea) return;
    const container = textarea.closest('.greeting-card-content');
    if (!container) return;

    const errorEl = container.querySelector('.js-greeting-card-error');
    const hintEl = container.querySelector('.greeting-card-required-hint');

    // Hide error, show hint
    if (errorEl) errorEl.style.display = 'none';
    if (hintEl) hintEl.style.display = 'block';

    // Remove error styling from textarea
    textarea.style.borderColor = 'var(--color-accent-porcelain, #D8D0C2)';
    textarea.style.boxShadow = 'none';
  }

  /**
   * Slide down animation
   */
  function slideDown(element) {
    element.style.display = 'block';
    element.style.height = '0';
    element.style.overflow = 'hidden';
    element.style.transition = `height ${CONFIG.ANIMATION_DURATION}ms ease-out`;

    // Trigger reflow
    element.offsetHeight;

    const height = element.scrollHeight;
    element.style.height = height + 'px';

    setTimeout(() => {
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
    }, CONFIG.ANIMATION_DURATION);
  }

  /**
   * Slide up animation
   */
  function slideUp(element) {
    element.style.height = element.scrollHeight + 'px';
    element.style.overflow = 'hidden';
    element.style.transition = `height ${CONFIG.ANIMATION_DURATION}ms ease-out`;

    // Trigger reflow
    element.offsetHeight;

    element.style.height = '0';

    setTimeout(() => {
      element.style.display = 'none';
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
    }, CONFIG.ANIMATION_DURATION);
  }

  /**
   * Remove existing greeting card from cart if present
   * Returns the line index of the removed card, or null if none found
   */
  async function removeExistingGreetingCard() {
    try {
      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Find existing greeting card by variant ID
      const existingCardIndex = cart.items.findIndex(item =>
        item.variant_id === CONFIG.GREETING_CARD_VARIANT_ID
      );

      if (existingCardIndex !== -1) {
        console.log('🗑️ Removing existing greeting card from cart...');

        // Remove the existing card (Shopify uses 1-based line numbers)
        const updateResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: existingCardIndex + 1,
            quantity: 0
          })
        });

        if (updateResponse.ok) {
          console.log('✅ Existing greeting card removed');
          return existingCardIndex;
        }
      }

      return null;
    } catch (error) {
      console.error('Error checking/removing existing greeting card:', error);
      return null;
    }
  }

  /**
   * Add greeting card to cart as separate line item
   * This function should be called when the main product is added to cart
   * IF the checkbox is checked
   *
   * IMPORTANT: Uses _skipInterceptor flag to prevent infinite loop
   * IMPORTANT: Only allows 1 greeting card in cart - removes existing if present
   */
  async function addGreetingCardToCart(message) {
    if (!message || message.trim() === '') {
      console.warn('No greeting card message provided');
      return Promise.resolve();
    }

    // Remove any existing greeting card first (max 1 allowed)
    await removeExistingGreetingCard();

    const properties = {
      '_greeting_card': 'true', // Hidden property to identify greeting card items
      '_kaartje_bericht': message
    };
    const messageLabel = window.translations?.greetingCard?.messageLabel || 'Card message';
    properties[messageLabel] = message;

    const formData = {
      items: [{
        id: CONFIG.GREETING_CARD_VARIANT_ID,
        quantity: 1,
        properties
      }]
    };

    console.log('Adding greeting card to cart:', formData);

    try {
      // Use XMLHttpRequest directly to bypass our own fetch interceptor
      // This prevents infinite loop where greeting card addition triggers another greeting card addition
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/cart/add.js');
        xhr.setRequestHeader('Content-Type', 'application/json');

        // Mark this request to skip our interceptor
        xhr._skipGreetingCardInterceptor = true;

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            console.log('✅ Greeting card added to cart:', data);
            resolve(data);
          } else {
            reject(new Error(`Failed to add greeting card: ${xhr.statusText}`));
          }
        };

        xhr.onerror = function() {
          reject(new Error('Network error adding greeting card'));
        };

        xhr.send(JSON.stringify(formData));
      });

    } catch (error) {
      console.error('❌ Error adding greeting card to cart:', error);
      throw error;
    }
  }

  /**
   * Hook into add-to-cart form submissions with CAPTURE phase
   * This runs BEFORE the app.js handler, allowing us to intercept properly
   */
  function hookAddToCartForms() {
    const forms = document.querySelectorAll('form[action="/cart/add"]');

    forms.forEach(form => {
      // Use CAPTURE phase (third parameter = true) to run before app.js handler
      form.addEventListener('submit', async function(e) {
        const toggle = this.querySelector('.js-greeting-card-toggle');
        const message = this.querySelector('.js-greeting-card-message');

        // If greeting card is checked but NO message, show error and prevent submission
        if (toggle && toggle.checked && message && !message.value.trim()) {
          console.log('🎨 Greeting card checked but no message - showing error');
          e.preventDefault();
          e.stopImmediatePropagation();
          showGreetingCardError(message);
          return;
        }

        // Only intercept if greeting card is checked AND has message
        if (!toggle || !toggle.checked || !message || !message.value.trim()) {
          return; // Let app.js handle it normally
        }

        console.log('🎨 Greeting card detected, intercepting form submission');
        e.preventDefault();
        e.stopImmediatePropagation(); // Prevent app.js handler from running

        try {
          // Get form data for main product
          const formData = new FormData(this);
          const productData = {};

          // Build the main product without card properties. The service line owns the message.
          formData.forEach((value, key) => {
            productData[key] = value;
          });

          console.log('📦 Adding main product...', productData);

          // Add main product first
          const mainResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(productData)
          });

          if (!mainResponse.ok) {
            const errorData = await mainResponse.json();
            throw new Error(errorData.description || 'Failed to add main product');
          }

          const mainData = await mainResponse.json();
          console.log('✅ Main product added:', mainData);

          // Then add greeting card
          console.log('💌 Adding greeting card...');
          await addGreetingCardToCart(message.value);

          // Fetch updated cart
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          console.log('🛒 Updated cart:', cart);

          // Use the theme's updateCart function if available
          if (typeof updateCart === 'function') {
            console.log('🔄 Using theme updateCart function');
            await updateCart(cart);
          } else {
            // Fallback: manually update cart UI
            console.log('🔄 Manually updating cart UI');

            // Update cart count in header
            const cartCount = document.querySelectorAll('.js-cart-count');
            cartCount.forEach(el => {
              el.textContent = cart.item_count;
            });

            // Open minicart (same as app.js does)
            document.body.classList.add('js-minicart-open');
          }

          console.log('✅ Product + Greeting Card successfully added to cart');

        } catch (error) {
          console.error('❌ Error adding items to cart:', error);
          alert(window.translations?.cart?.errorAdding || 'Er is een fout opgetreden bij het toevoegen aan de winkelwagen. Probeer het opnieuw.');
        }
      }, true); // TRUE = capture phase, runs BEFORE bubble phase
    });
  }

  /**
   * Global fetch/XHR interceptor for /cart/add.js calls
   * This catches ALL cart additions regardless of source (fetch OR jQuery AJAX)
   * SAFE VERSION - Uses proper error handling and doesn't break on exceptions
   */
  function interceptCartAddRequests() {
    console.log('🔧 Installing request interceptors...');

    // Intercept fetch API
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      try {
        const [url, options] = args;

        // Check if this is a cart/add request
        if (url && url.includes('/cart/add.js')) {
          console.log('🔍 [FETCH] Intercepted cart/add.js request:', url);

          // Check if greeting card is selected
          const toggle = document.querySelector('.js-greeting-card-toggle:checked');
          const message = document.querySelector('.js-greeting-card-message');

          // If greeting card is checked but no message, block and show error
          if (toggle && message && !message.value.trim()) {
            console.log('🚫 [FETCH] Greeting card checked but no message - blocking request');
            showGreetingCardError(message);
            throw new Error(window.translations?.greetingCard?.messageRequired || 'Vul een bericht in voor je kaartje');
          }

          if (toggle && message && message.value.trim()) {
            console.log('💌 Greeting card detected, will add as separate line item');

            // Send original request for main product (message is only on Kaartje Service line item)
            const response = await originalFetch.call(this, url, options);

            // If successful, add greeting card
            if (response.ok) {
              console.log('✅ Main product added, now adding greeting card...');
              try {
                await addGreetingCardToCart(message.value);
                console.log('✅ Greeting card successfully added');

                // Refresh cart count and minicart
                const cartResponse = await fetch('/cart.js');
                const cart = await cartResponse.json();
                document.querySelectorAll('.js-cart-count').forEach(el => {
                  el.textContent = cart.item_count;
                });

                // Refresh minicart if function available
                if (typeof window.refreshMinicart === 'function') {
                  await window.refreshMinicart();
                }
              } catch (error) {
                console.error('❌ Failed to add greeting card:', error);
              }
            }

            return response;
          }
        }

        // No greeting card, proceed normally
        return originalFetch.apply(this, args);
      } catch (error) {
        // If ANY error occurs in our interceptor, fall back to original fetch
        console.error('Greeting card interceptor error, falling back:', error);
        return originalFetch.apply(this, args);
      }
    };

    // Intercept XMLHttpRequest (for jQuery $.ajax)
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._url = url;
      this._method = method;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function(body) {
      try {
        // Skip interception if this is our own greeting card addition call
        if (this._skipGreetingCardInterceptor) {
          console.log('⏩ Skipping interceptor for greeting card addition (preventing loop)');
          return originalSend.call(this, body);
        }

        // Check if this is a cart/add request
        if (this._url && this._url.includes('/cart/add.js')) {
          console.log('🔍 [XHR] Intercepted cart/add.js request:', this._url);

          // Check if greeting card is selected
          const toggle = document.querySelector('.js-greeting-card-toggle:checked');
          const message = document.querySelector('.js-greeting-card-message');

          // If greeting card is checked but no message, block and show error
          if (toggle && message && !message.value.trim()) {
            console.log('🚫 [XHR] Greeting card checked but no message - blocking request');
            showGreetingCardError(message);
            // Abort the request
            this.abort();
            return;
          }

          if (toggle && message && message.value.trim()) {
            console.log('💌 Greeting card detected, will add as separate line item');

            // Store greeting card info for after main request completes
            const greetingMessage = message.value;
            let greetingCardAdded = false; // Prevent duplicate additions

            // Signal that greeting card will be added (app.js should wait)
            window._greetingCardPending = true;

            // Hook into readystatechange to add greeting card after main product
            const originalOnReadyStateChange = this.onreadystatechange;

            this.onreadystatechange = function() {
              try {
                if (this.readyState === 4 && this.status >= 200 && this.status < 300 && !greetingCardAdded) {
                  greetingCardAdded = true;
                  console.log('✅ Main product added via XHR, now adding greeting card...');
                  addGreetingCardToCart(greetingMessage)
                    .then(async () => {
                      console.log('✅ Greeting card successfully added');
                      // Signal completion
                      window._greetingCardPending = false;

                      // Refresh the minicart using the global function
                      try {
                        // Update cart count first
                        const cartResponse = await fetch('/cart.js');
                        const cart = await cartResponse.json();
                        document.querySelectorAll('.js-cart-count').forEach(el => {
                          el.textContent = cart.item_count;
                        });

                        // Use global refreshMinicart if available (defined in base-minicart.liquid)
                        if (typeof window.refreshMinicart === 'function') {
                          console.log('🔄 Refreshing minicart via global function...');
                          await window.refreshMinicart();
                        } else {
                          // Fallback: dispatch event
                          console.log('🔄 Dispatching greetingCardAdded event...');
                          window.dispatchEvent(new CustomEvent('greetingCardAdded'));
                        }
                      } catch (e) {
                        console.error('Failed to refresh cart after greeting card:', e);
                      }
                    })
                    .catch(error => {
                      console.error('❌ Failed to add greeting card:', error);
                      window._greetingCardPending = false;
                    });
                }

                if (originalOnReadyStateChange) originalOnReadyStateChange.apply(this, arguments);
              } catch (error) {
                console.error('Error in onreadystatechange:', error);
                window._greetingCardPending = false;
                if (originalOnReadyStateChange) originalOnReadyStateChange.apply(this, arguments);
              }
            };

            // Send original body (message is only on Kaartje Service line item)
            return originalSend.call(this, body);
          }
        }

        return originalSend.call(this, body);
      } catch (error) {
        // If ANY error occurs in our interceptor, fall back to original send
        console.error('Greeting card XHR interceptor error, falling back:', error);
        return originalSend.call(this, body);
      }
    };

    console.log('✅ Request interceptors installed (fetch + XHR)');
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    console.log('🎨 Greeting card script initializing...');
    initGreetingCardToggle();
    hookAddToCartForms();
    console.log('✅ Greeting card script initialized');
  }

  // Re-enabled after fixing Front Chat click blocking issue
  interceptCartAddRequests();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for potential external use
  window.GreetingCard = {
    addToCart: addGreetingCardToCart,
    config: CONFIG,
    init: init
  };

})();
